/**
 * Install Algolia to system
 * 
 * 1. Install packages to packages/web folder: 
 *      npm i algoliasearch gatsby-plugin-algolia
 * 
 * 2. Add environment variables:
 *      ALGOLIA_APP_ID=XXX
 *      ALGOLIA_API_KEY=XXX
 *      ALGOLIA_INDEX_NAME=XXX
 * 
 * 3. Add the following to the top of `gatsby-config.js` above the module.exports:
        const fs = require('fs');

        const AlgoliaQuery = `{
            products: allBigCommerceProducts {
                nodes {
                    id
                    entityId: bigcommerce_id
                    objectID: bigcommerce_id
                    name
                    sku
                    price
                    calculated_price
                    sale_price
                    brand_id
                    description
                    inventory_level
                    inventory_tracking
                    custom_url {
                        url
                        is_customized
                    }
                    images {
                        url_standard
                        url_thumbnail
                        url_zoom
                        id
                        description
                        image_file
                        is_thumbnail
                    }
                    categories
                    variants {
                        product_id
                        id
                        sku
                    }
                    custom_fields {
                        name
                        value
                    }
                }
            }
            
            brands: allBigCommerceBrands {
                edges {
                    node {
                        name
                        id
                        entityId: bigcommerce_id
                    }
                }
            }

            categories: allBigCommerceCategories {
                edges {
                    node {
                        entityId: bigcommerce_id
                        name
                        image_url
                        description
                        parent_id
                        custom_url {
                        url
                        is_customized
                        }
                    }
                }
            }
        }`;

        const queries = [
            {
                query: AlgoliaQuery,
                transformer: async ({ data }) => {
                    let _json = data.products.nodes;
                    // create list of filters
                    let _filterName = [];

                    const dataBrands = data.brands.edges.map(c => c.node)
                    const dataCategories = data.categories.edges.map(c => c.node)

                    const _result = _json.map((a) => {
                        const __customFields = a.custom_fields;
                        const __filterName = __customFields.filter(b => b.name === 'Filter Name').map(b => b.value);

                        try {
                            _filterName = [..._filterName, ...__filterName];
                        } catch (_e) {
                            console.log(_e);
                        }

                        return {
                            id: a.id,
                            entityId: a.entityId,
                            objectID: a.entityId,
                            name: a.name,
                            sku: a.sku,
                            price: a.price,
                            calculated_price: a.calculated_price,
                            sale_price: a.sale_price,
                            brand_id: a.brand_id,
                            brand_name: dataBrands.filter(b => b.entityId === a.brand_id).length ? dataBrands.filter(b => b.entityId === a.brand_id)[0].name : '',
                            description: a.description,
                            custom_url: a.custom_url,
                            defaultImage: {
                                url: a.images.filter(b => b.is_thumbnail).length ? a.images.filter(b => b.is_thumbnail)[0].url_thumbnail : ''
                            },
                            images: a.images,
                            filterName: __filterName,
                            variants: a.variants,
                            product_categories: dataCategories.filter(b => a.categories.indexOf(b.entityId) > -1).map(b => {
                                return {
                                    entityId: b.entityId,
                                    name: b.name,
                                    path: b.custom_url.url
                                }
                            }),
                            customFields: a.custom_fields,
                        };
                    });

                    try {
                        _filterName = [...new Set(_filterName)];
                        
                        const _filters = [
                            {
                                label: 'Filter Name',
                                field: 'filterName',
                                type: 'multiselect',
                                collapse: 'expanded',
                                values: _filterName.map(b => ({key: b, label: b}))
                            },
                        ];
                        fs.writeFile('./src/components/organisms/Filters/FilterData.json', JSON.stringify(_filters), err => {
                            if (err) {
                                console.error(err)
                                return
                            }
                        });
                    } catch (_e) {
                        console.log(_e);
                    }

                    return _result;
                }, // optional
                // indexName: 'index name to target', // overrides main index name, optional
                settings: {
                    // optional, any index settings
                    // Note: by supplying settings, you will overwrite all existing settings on the index
                    attributesForFaceting: [
                        'filterName', 'product_categories.name', 'product_categories.entityId'
                    ],
                    searchableAttributes: [
                        'name', 'description', 'product_categories.name', 'filterName'
                    ]
                },
                // matchFields: ['slug', 'modified'], // Array<String> overrides main match fields, optional
            },
        ];

 * 4. Update the `AlgoliaQuery` with the data that needs to be searchable and returned in the results
 * 5. Add the following to the `plugins` in `gatsby-config.js` at the very end. Most important that its last in the list:
        {
            // This plugin must be placed last in your list of plugins to ensure that it can query all the GraphQL data
            resolve: `gatsby-plugin-algolia`,
            options: {
                appId: process.env.ALGOLIA_APP_ID,
                // Use Admin API key without GATSBY_ prefix, so that the key isn't exposed in the application
                // Tip: use Search API key with GATSBY_ prefix to access the service from within components
                apiKey: process.env.ALGOLIA_API_KEY,
                indexName: process.env.ALGOLIA_INDEX_NAME, // for all queries
                queries,
                chunkSize: 10000, // default: 1000
                settings: {
                // optional, any index settings
                // Note: by supplying settings, you will overwrite all existing settings on the index
                },
                enablePartialUpdates: false, // default: false
                matchFields: ['objectID', 'slug', 'modified'], // Array<String> default: ['modified']
                concurrentQueries: false, // default: true
                skipIndexing: false, // default: false, useful for e.g. preview deploys or local development
                continueOnFailure: false // default: false, don't fail the build if algolia indexing fails
            },
        },

 * 6. Enable this component in the `ProcessSearch.js` component.
 */

import algoliarecommend from '@algolia/recommend';
import algoliasearch from 'algoliasearch';

require('dotenv').config();

const GetAlgoliaRecommendations = ({ objectId }) => {
  const client = algoliarecommend(
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY
  );
  client
    .getRelatedProducts([
      {
        indexName: process.env.GATSBY_ALGOLIA_INDEX_NAME,
        objectID: objectId ?? []
      }
    ])
    .then(({ results }) => {
      console.log(results);
    })
    .catch(err => {
      console.log(err);
    });
};

const GetAlgoliaSerachSuggestions = ({ keyWord }) => {
  // const searchClient = algoliasearch(appId, apiKey);
  // const querySuggestionsPlugin = createQuerySuggestionsPlugin({
  //     searchClient,
  //     indexName: 'instant_search_demo_query_suggestions',
  //     getSearchParams({ state }) {
  //       return { hitsPerPage: state.query ? 5 : 10 };
  //     },
  //   });
  //   autocomplete({
  //   })
};

const AlgoliaSearch = async (searchTerm, limit = 100, facetFilters = {}) => {
  const client = algoliasearch(
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY
  );

  // Define your sorting strategies
  const sortBy = {
    best: 'prod_DEFAULT', //process.env.ALGOLIA_INDEX_NAME,
    newest: 'Production_Products_Newest', //process.env.ALGOLIA_INDEX_NAME,
    A2Z: process.env.GATSBY_ALGOLIA_INDEX_NAME_A2Z ?? 'Production_Products_Name_Asc',
    Z2A: process.env.GATSBY_ALGOLIA_INDEX_NAME_Z2A ?? 'Production_Products_Name_Desc',
    PriceAsc: process.env.GATSBY_ALGOLIA_INDEX_NAME_PRICE_ASC ?? 'Production_Products_Price_Asc',
    PriceDesc: process.env.GATSBY_ALGOLIA_INDEX_NAME_PRICE_DESC ?? 'Production_Products_Price_Desc'
  };

  // Get the sort strategy from facetFilters
  const sortStrategy = facetFilters.sortBy || 'best'; // default to 'newest' if no sortBy is provided

  // Get the index for the chosen sort strategy
  const indexName = sortBy[sortStrategy];
  const index = client.initIndex(indexName);

  delete facetFilters['sortBy'];

  let rangeNumFilters = [];
  if (typeof facetFilters.rangeNumFilters !== 'undefined') {
    rangeNumFilters = facetFilters['rangeNumFilters'];
    delete facetFilters['rangeNumFilters'];
  }

  if (typeof facetFilters.page === 'undefined') {
    facetFilters.page = 1;
  }

  const parseFilters = {
    facets: [
      'product_categories.name',
      'brand_name',
      'pets',
      'healthCondition',
      'flavour',
      'foodType',
      'lifeStage'
    ],
    hitsPerPage: limit,
    numericFilters: [...rangeNumFilters],
    ...facetFilters
  };
  return new Promise(res => {
    index.search(searchTerm, parseFilters).then(response => {
      res({ ...response });
    });
  });
};

export { AlgoliaSearch, GetAlgoliaRecommendations };
